// Arquivo: src/domain/engineering.ts  (colar o conteúdo INTEGRAL no Code Mode)
// Domínio do Dash Engenharia: tipos do contrato get-dashboard, ordenação EAP
// numérica, classificação do IDC e regras derivadas de exibição.

export type Role = 'admin' | 'consulta'

export interface SnapshotRef {
  snapshotId: string
  runId: string
  status: string
  ativo: boolean
  publicado: boolean
  fonte: string
  dataCorte: string | null
  periodoInicio: string | null
  periodoFim: string | null
  publicadoEm?: string | null
}

export interface JobInfo {
  jobId: string
  runId: string
  status: 'PENDENTE' | 'PROCESSANDO' | 'APROVADO' | 'REPROVADO' | 'ERRO'
  progresso?: number
  etapa?: string
  erros?: string[]
}

// Dinheiro sempre como string decimal ("1234.56"); ausente = undefined.
export interface Kpis {
  orcado?: string
  realizado?: string
  realizadoMenosEstoque?: string
  realizadoPeriodo?: string
  comprometido?: string
  saldoIncorrer?: string
  aComprometer?: string
  tendencia?: string
  estoque?: string
  resultado?: string
  budgetIdc?: string
  estoqueAtualMes?: string | null
  estoqueMax?: string | null
  estoqueMaxMes?: string | null
  estoqueMin?: string | null
  estoqueMinMes?: string | null
  resultadoPct?: number | null
  estoquePct?: number | null
  avancoFisico?: number | null
  avancoFonte?: 'prevision' | 'sienge' | 'financeiro' | 'FALLBACK_FINANCEIRO' | 'PREVISION' | null
  idc?: number | null
  unidades?: number | null
  areaPrivativa?: number | null
  custoUh?: number | null
  custoM2?: number | null
}

export interface EtapaResumo {
  etapa: string
  orcado: string
  realizado: string
  tendencia: string
  resultado: string
}

export interface ObraResumo {
  obraId: string
  obraNome: string
  orcado: string
  realizado: string
  comprometido: string
  tendencia: string
  resultado: string
  saldoIncorrer: string
  aComprometer: string
  realizadoPeriodo: string
  estoque: string
  avanco: number | null
  avancoFonte: string | null
  pctPeriodo: number | null
  unidades: number | null
  areaPrivativa: number | null
}

export interface MensalPonto {
  mes: string
  total: string
  estoque: string
  pctEstoqueOrcado: number
}

export interface TreeNode {
  id: string
  codigo: string
  nome: string
  descricao: string
  nivel: number
  obraId: string
  orcado: string
  realizado: string
  comprometido: string
  aComprometer: string
  tendencia: string
  saldo: string
  estoque: string
  resultado: string
  pctResultado: number | null
  saldoOrcamento: string
  medido: number | null
  children: TreeNode[]
}

export interface LancamentoRow {
  obraId: string
  obraNome: string
  wbsCodigo: string
  insumoCodigo: string
  insumoDescricao: string
  dataContabil: string | null
  documento: string
  tituloParcela: string | null
  credor: string
  origem: string
  valor: string
  valorDocumento: string | null
}

export interface DetailPage {
  total: number
  limit: number
  offset: number
  rows: LancamentoRow[]
}

export interface FiltersInfo {
  obras: Array<{ id: string; name: string }>
  unidades: Array<{ obraId: string; unidadeId: string; unidadeNome: string }>
  dataInicio: string | null
  dataFim: string | null
}

export interface DashboardResponse {
  snapshot: SnapshotRef
  kpis: Kpis
  obrasResumo: ObraResumo[]
  etapas: EtapaResumo[]
  desviosPositivos: EtapaResumo[]
  desviosNegativos: EtapaResumo[]
  topSubetapas: Array<{ subetapa: string; total: string }>
  topSubetapasEstoque: Array<{ subetapa: string; total: string }>
  mensal: MensalPonto[]
  avisos: string[]
  truncated: boolean
}

export interface TreeResponse {
  nodes: TreeNode[]
  avisos: string[]
}

export interface InsumosResponse {
  rows: LancamentoRow[]
  total: number
  avisos: string[]
}

export interface SnapshotResponse {
  snapshot: SnapshotRef | null
  job: JobInfo | null
  avisos: string[]
}

export interface DataFilters {
  obraId: string
  unidadeId: string
  inicio: string
  fim: string
}

export const EMPTY_FILTERS: DataFilters = { obraId: '', unidadeId: '', inicio: '', fim: '' }

export const ORIGEM_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Todas as Origens' },
  { value: 'ME', label: 'ME — Medição' },
  { value: 'AC', label: 'AC — Apropriação Contratual' },
  { value: 'CP', label: 'CP — Contas a Pagar' },
  { value: 'NF', label: 'NF — Nota Fiscal' },
  { value: 'PE', label: 'PE — Pedido' },
  { value: 'CX', label: 'CX — Caixa' },
  { value: 'FP', label: 'FP — Folha de Pagamento' },
  { value: 'BC', label: 'BC — Banco / Débito' },
  { value: 'AI', label: 'AI — Apropriação Indireta' },
  { value: 'CO', label: 'CO — Contrato' },
]

// Ordenação EAP numérica por segmentos: 2.2.1.1 < 2.2.1.2 < 2.2.1.10
export function naturalSortKey(code: string): number[] {
  return String(code ?? '')
    .split(/[^0-9]+/)
    .filter(part => part !== '')
    .map(part => Number.parseInt(part, 10))
    .filter(n => Number.isFinite(n))
}

export function compareNatural(a: string, b: string): number {
  const ka = naturalSortKey(a)
  const kb = naturalSortKey(b)
  const len = Math.max(ka.length, kb.length)
  for (let i = 0; i < len; i += 1) {
    const va = ka[i] ?? -1
    const vb = kb[i] ?? -1
    if (va !== vb) return va - vb
  }
  return String(a).localeCompare(String(b), 'pt-BR')
}

// Fração 0–1 a partir de 0–100 ou 0–1.
export function normalizeAvanco(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return 0
  const fraction = n > 1 ? n / 100 : n
  return Math.min(1, Math.max(0, fraction))
}

export function idcPill(idc: number | null | undefined): { label: string; className: string } {
  if (idc === null || idc === undefined || !Number.isFinite(idc)) return { label: 'Sem base', className: 'kpi-pill muted' }
  if (idc >= 1) return { label: 'Eficiente', className: 'kpi-pill ok' }
  if (idc >= 0.8) return { label: 'Alerta', className: 'kpi-pill warn' }
  return { label: 'Crítico', className: 'kpi-pill bad' }
}

export function avancoFonteLabel(fonte?: string | null): string {
  switch (fonte) {
    case 'prevision':
    case 'PREVISION':
      return 'Prevision (GraphQL)'
    case 'sienge':
      return '% Medido Sienge'
    case 'financeiro':
    case 'FALLBACK_FINANCEIRO':
      return 'Fallback financeiro (Realizado/Orçado)'
    default:
      return 'Sem medição registrada'
  }
}

export function workStatusBadge(desvioPct: number | null): { label: string; className: string } | null {
  if (desvioPct === null || !Number.isFinite(desvioPct)) return null
  if (desvioPct > 25) return { label: 'Estouro', className: 'trend-status bad' }
  if (desvioPct > 0) return { label: 'Atenção', className: 'trend-status warn' }
  return { label: 'No Orçamento', className: 'trend-status ok' }
}
